import type {
  CommonValue,
  ExportValue,
  ObjectFieldValue,
  ReservationValue,
} from 'types/reservationSettingsColumnValues.type';
import { createSortedFieldOrTypeOptions } from 'utils/objectTypes';
import type { TField, TReservationTemplate, TType } from '@timeedit/types';
import { TEInput } from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import { getText } from '../../../i18n/intl';
import './FieldsList.scss';

type CommonValueProps = {
  type: CommonValue;
  onChange: (newValue: any) => void;
  reservationTemplate?: TReservationTemplate;
  objectTypeIds?: (string | number)[];
  allTypes: TType[];
  allFields: TField[];
  fieldLabel?: string;
  objectLabel?: string;
  valueLabel?: string;
  hasError: boolean;
  extraOptions?: DefaultOptionType[];
  lockedType?: boolean;
  lockedTypeId?: string;
};

export const CommonValueInput = (props: CommonValueProps) => {
  const { type, ...restProps } = props;
  if (!props.type?.type) {
    return null;
  }
  switch (props.type.type) {
    case 'object-field':
      return <ObjectFieldValueInput {...restProps} {...props.type} />;
    case 'reservation':
      return <ReservationValueInput {...restProps} {...props.type} />;
    case 'export':
      return <ExportValueInput {...restProps} {...props.type} />;
    case 'text':
      return (
        <TEInput.TextArea
          label={props.valueLabel}
          onChange={props.onChange}
          value={props.type.value}
          status={props.hasError ? 'error' : undefined}
        />
      );
    default:
      return null;
  }
};

type ReservationValueInputProps = ReservationValue & {
  onChange: (newValue: any) => void;
  reservationTemplate?: TReservationTemplate;
  allFields: TField[];
  fieldLabel?: string;
  hasError: boolean;
  extraOptions?: DefaultOptionType[];
};

const ReservationValueInput = (props: ReservationValueInputProps) => {
  const templateFieldIds = new Set(props.reservationTemplate?.fields ?? []);

  const templateFields =
    props.reservationTemplate === undefined
      ? props.allFields
      : props.allFields.filter((field) => templateFieldIds.has(field.id));

  const reservationFieldOptions =
    createSortedFieldOrTypeOptions(templateFields);

  return (
    <TEInput.Select
      label={props.fieldLabel ?? getText('field')}
      value={props.value}
      onChange={props.onChange}
      options={[...reservationFieldOptions, ...(props.extraOptions ?? [])]}
      status={props.hasError ? 'error' : undefined}
      showSearch
      optionFilterProp="label"
    />
  );
};

type ExportValueInputProps = ExportValue & {
  onChange: (newValue: any) => void;
  reservationTemplate?: TReservationTemplate;
  allFields: TField[];
  fieldLabel?: string;
  hasError: boolean;
};

const ExportValueInput = (props: ExportValueInputProps) => {
  return (
    <TEInput.Select
      label={props.fieldLabel ?? getText('field')}
      value={props.value}
      onChange={props.onChange}
      options={[
        { value: 'date', label: 'Date of export' },
        { value: 'time', label: 'Time of export' },
        { value: 'date-time', label: 'Date & Time of export' },
      ]}
      status={props.hasError ? 'error' : undefined}
      showSearch
      optionFilterProp="label"
    />
  );
};

type ObjectFieldValueInputProps = ObjectFieldValue & {
  onChange: (newValue: any) => void;
  allTypes: TType[];
  allFields: TField[];
  fieldLabel?: string;
  objectLabel?: string;
  objectTypeIds?: (string | number)[];
  hasError: boolean;
  lockedType?: boolean;
  lockedTypeId?: string;
};
export const ObjectFieldValueInput = (props: ObjectFieldValueInputProps) => {
  const objectTypeIdsSet = new Set(props.objectTypeIds ?? []);

  const objectTypes =
    props.objectTypeIds === undefined
      ? props.allTypes
      : props.allTypes.filter((type) => objectTypeIdsSet.has(type.id));
  const typeOptions = createSortedFieldOrTypeOptions(objectTypes);

  const [baseTypeId, fieldId] = props.value?.split('-') ?? [
    undefined,
    undefined,
  ];
  const typeId = props.lockedType ? props.lockedTypeId : baseTypeId;

  const objectType = objectTypes.find((type) => type.id.toString() === typeId);

  const objectFieldIds = new Set(
    (objectType?.fields ?? []).concat(objectType?.parentFields ?? [])
  );

  const objectFields = props.allFields?.filter((field) =>
    objectFieldIds.has(field.id)
  );
  const objectFieldsOptions = createSortedFieldOrTypeOptions(objectFields);

  const objectError = props.hasError && typeId == null;
  const fieldError = props.hasError && fieldId == null;

  return (
    <Space direction="vertical" className="fields-list-full-width">
      <TEInput.Select
        label={props.objectLabel ?? getText('objects_type')}
        disabled={props.lockedType ? true : false}
        size="small"
        onChange={(val) => {
          if (val == null) return;
          props.onChange(`${val}`);
        }}
        onClear={() => props.onChange(null)}
        value={typeId}
        options={typeOptions}
        status={objectError ? 'error' : undefined}
        allowClear={false}
        showSearch
        optionFilterProp="label"
      />
      <TEInput.Select
        label={props.fieldLabel ?? getText('field')}
        value={fieldId}
        allowClear={false}
        onChange={(val) => {
          if (val == null) return;
          props.onChange(
            typeId != null && val != '' ? `${typeId}-${val}` : null
          );
        }}
        onClear={() => props.onChange(typeId != null ? typeId : null)}
        options={objectFieldsOptions}
        status={fieldError ? 'error' : undefined}
        showSearch
        optionFilterProp="label"
      />
    </Space>
  );
};
